import CompanySettingsService from "app/data/company-settings/companySettingsService";
import { PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";

/**
 * Normalize priority-customers API payload to a list of account numbers.
 * Supports: number[] | { accountNumbers: number[] }
 */
export function normalizePriorityAccountNumbers(data: unknown): number[] {
  if (Array.isArray(data)) {
    return data
      .map((v) => (typeof v === "number" ? v : Number(v)))
      .filter((n) => Number.isFinite(n));
  }
  if (
    data &&
    typeof data === "object" &&
    Array.isArray((data as { accountNumbers?: unknown }).accountNumbers)
  ) {
    return ((data as { accountNumbers: unknown[] }).accountNumbers)
      .map((v) => (typeof v === "number" ? v : Number(v)))
      .filter((n) => Number.isFinite(n));
  }
  return [];
}

export function collectUniqueShipperAccountNumbers(
  rows: PlanningFreightProbill[]
): number[] {
  const ids = new Set<number>();
  for (const row of rows) {
    const n = Number(row.shipperNumber);
    if (Number.isFinite(n) && n > 0) ids.add(n);
  }
  return [...ids];
}

/**
 * After freight list (AS400/linehaul) loads:
 * 1) collect unique shipperNumbers
 * 2) POST /accounts/priority-customers { accountNumbers }
 * 3) merge priorityCustomer onto each row for ★ display
 *
 * Fails soft: if priority API errors, returns original rows (no stars).
 */
export async function enrichPlanningFreightWithPriorityCustomers(
  rows: PlanningFreightProbill[]
): Promise<PlanningFreightProbill[]> {
  if (!rows.length) return rows;

  const accountNumbers = collectUniqueShipperAccountNumbers(rows);
  if (!accountNumbers.length) {
    return rows.map((row) => ({ ...row, priorityCustomer: false }));
  }

  try {
    const response = await CompanySettingsService.getInstance().getPriorityCustomers(
      accountNumbers
    );
    if (!response.ok()) {
      return rows;
    }
    const prioritySet = new Set(normalizePriorityAccountNumbers(response.data));
    return rows.map((row) => {
      const shipperNumber = Number(row.shipperNumber);
      return {
        ...row,
        priorityCustomer:
          Number.isFinite(shipperNumber) && prioritySet.has(shipperNumber),
      };
    });
  } catch {
    // Priority enrichment is best-effort; still show freight list.
    return rows;
  }
}
